import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@apollo/client";
import {
  Loader,
  ModalButtonsContainer,
  ModalContext,
  showErrorDialog,
  TextButton,
} from "@SeemplicityDev/shared";
import type { ScopeNode } from "../../../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";
import useLogger from "../../../../Logger/useLogger";
import { resourceTypes } from "../../../FindingsUtils";
import { GET_GROUP_BY_ID, parseGroupData } from "../ScopesApi";
import EditScopeGroupDetails, {
  type EditScopeGroupDetailsProps,
} from "./EditScopeGroupDetails";
import { useEditScopeGroup } from "./useScopeGuardrailMutations";
import { useScopeDependencies } from "./useScopeDependencies";
import {
  asTreeNodes,
  collisionMessage,
  editableMembersTree,
  findAncestorIds,
} from "./scopeTreeUtils";

export interface EditedGroupMembers {
  childGroupIds: string[];
  childScopeIds: string[];
}

export interface EditScopeGroupModalProps {
  groupId: string;
  groupName?: string;
  root?: ScopeNode | null;
  onEdited: (name: string, members: EditedGroupMembers) => void;
}

interface GroupByIdResponse {
  scope_group_by_id?: { group_data?: string | null } | null;
}

interface Member {
  id: string;
  type: string;
}

const EDIT_FAILED_MESSAGE =
  "Couldn't update this scope group. Please try again.";

// null, not [], when unreadable: the edit sends the full membership, so
// "no members" would empty the group.
const parseSavedMembers = (raw: string | null | undefined): Member[] | null => {
  const data = parseGroupData(raw);
  if (!data) {
    return null;
  }
  return [
    ...(data.child_groups ?? []).map(({ id }) => ({
      id,
      type: resourceTypes.GROUP,
    })),
    ...(data.child_scopes ?? []).map(({ id }) => ({
      id,
      type: resourceTypes.SCOPE,
    })),
  ];
};

const EditScopeGroupModal: React.FC<EditScopeGroupModalProps> = ({
  groupId,
  groupName,
  root,
  onEdited,
}) => {
  const { closeModal } = useContext(ModalContext);
  const { logWarning } = useLogger();
  const { editScopeGroup, loading: saving } = useEditScopeGroup();
  const {
    check,
    called: checkCalled,
    loading: checking,
    dependencies,
    error: checkError,
  } = useScopeDependencies();
  const {
    data,
    loading: loadingMembers,
    error: membersError,
    refetch: refetchMembers,
  } = useQuery<GroupByIdResponse>(GET_GROUP_BY_ID, {
    variables: { group_id: groupId },
    fetchPolicy: "network-only",
  });

  const runCheck = useCallback(
    () => check({ id: groupId, nodeType: "group", includeDescendants: true }),
    [groupId, check]
  );
  useEffect(() => {
    runCheck();
  }, [runCheck]);

  const [name, setName] = useState(groupName ?? "");
  // null until the picker is touched.
  const [members, setMembers] = useState<Member[] | null>(null);
  // The group's own name is taken: shown under the Name input.
  const [nameCollision, setNameCollision] = useState<string>();
  // Dropped members collide at the top level: shown under the picker.
  const [collision, setCollision] = useState<string>();
  // `saving` flips a render late; this blocks a double-click.
  const inFlight = useRef(false);

  const savedMembers = useMemo(
    () => parseSavedMembers(data?.scope_group_by_id?.group_data),
    [data]
  );
  const tree = useMemo(() => editableMembersTree(root), [root]);
  const excludedIds = useMemo(
    () => [groupId, ...findAncestorIds(root, groupId)],
    [root, groupId]
  );
  const inUseIds = useMemo(
    () => dependencies?.descendants_in_use?.map((ref) => ref.id) ?? [],
    [dependencies]
  );

  const onNameChange = (value: string) => {
    setName(value);
    setNameCollision(undefined);
    setCollision(undefined);
  };

  const onMembersChange: EditScopeGroupDetailsProps["onMembersChange"] = (
    checked
  ) => {
    setMembers(
      checked.map((node) => ({ id: String(node.id), type: String(node.type) }))
    );
    setCollision(undefined);
  };

  const confirm = () => {
    const chosen = members ?? savedMembers;
    if (inFlight.current || !chosen) {
      return;
    }
    inFlight.current = true;
    const trimmed = name.trim();
    const edited: EditedGroupMembers = {
      childGroupIds: chosen
        .filter((m) => m.type === resourceTypes.GROUP)
        .map((m) => m.id),
      childScopeIds: chosen
        .filter((m) => m.type === resourceTypes.SCOPE)
        .map((m) => m.id),
    };
    editScopeGroup(groupId, { name: trimmed, ...edited })
      .then(({ ok, nameCollisions }) => {
        if (ok) {
          onEdited(trimmed, edited);
          closeModal();
          return;
        }
        // A refusal is only ever a name collision: the group's new name, a
        // dropped member's name at the top level, or both.
        const own = nameCollisions.filter(
          (c) => c.node_type === "group" && c.name === trimmed
        );
        const droppedCollisions = nameCollisions.filter(
          (c) => c.node_type !== "group" || c.name !== trimmed
        );
        setNameCollision(
          own.length || !droppedCollisions.length
            ? collisionMessage(own, "group", trimmed)
            : undefined
        );
        setCollision(
          droppedCollisions.length
            ? collisionMessage(droppedCollisions, "group", trimmed)
            : undefined
        );
      })
      .catch((err: unknown) => {
        logWarning(err, "Scope guardrails: edit_scope_group_v3 failed");
        showErrorDialog({ message: EDIT_FAILED_MESSAGE });
      })
      .finally(() => {
        inFlight.current = false;
      });
  };

  const loading = loadingMembers || !checkCalled || checking;
  // Never edit without the saved members or the in-use check.
  const unavailable =
    !loading && (!!membersError || savedMembers === null || !!checkError);

  if (loading) {
    return (
      <div className={"modal-text scope-guardrail-modal-placeholder"}>
        <Loader center title={"Loading scope group..."} />
      </div>
    );
  }

  if (unavailable) {
    return (
      <div className={"modal-text scope-guardrail-modal-placeholder"}>
        <Loader
          center
          title={"Couldn't load this scope group."}
          subtitle={"Please try again."}
        />
        <ModalButtonsContainer centerButtons>
          <TextButton
            buttonStyle={"secondary"}
            label={"Close"}
            onClick={closeModal}
            data-testid={"close-edit-scope-group"}
          />
          <TextButton
            buttonStyle={"primary"}
            label={"Retry"}
            onClick={() => {
              refetchMembers();
              runCheck();
            }}
            data-testid={"retry-edit-scope-group"}
          />
        </ModalButtonsContainer>
      </div>
    );
  }

  return (
    <EditScopeGroupDetails
      name={name}
      onNameChange={onNameChange}
      tree={tree}
      excludedIds={excludedIds}
      expandedIds={excludedIds}
      inUseIds={inUseIds}
      // The picker re-applies these on every search, so they track the user's picks.
      preCheckedNodes={asTreeNodes((members ?? savedMembers) as ScopeNode[])}
      onMembersChange={onMembersChange}
      nameError={nameCollision}
      collisionError={collision}
      changeDisabled={saving}
      onConfirm={confirm}
      onCancel={closeModal}
    />
  );
};

export default EditScopeGroupModal;
